import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import CallMadeIcon from "@material-ui/icons/CallMade";
import CallReceivedIcon from "@material-ui/icons/CallReceived";
import GetAppIcon from "@material-ui/icons/GetApp";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TablePaginationActions from "../../components/TablePaginationActions";
import { TagsFilter } from "../../components/TagsFilter";
import TicketModal from "../../components/TicketModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { exportToExcel } from "../../utils/exportToExcel";
import { getMessagePreview } from "../../utils/messagePreview";
import { i18n } from "../../translate/i18n";

// Mesmo mínimo do backend (ListMessagesReportService.MIN_MEANINGFUL_CHARS):
// abaixo disso o termo nem sai do navegador, evitando um 400 por tecla digitada.
const MIN_TERM_CHARS = 3;
// Função (não constante de módulo) para o rótulo seguir o idioma em uso.
const getNoUserOption = () => ({ id: 0, name: i18n.t("messagesReport.noUser") });

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    padding: theme.spacing(1),
    ...theme.scrollbarStyles
  },
  filtersRow: {
    marginBottom: theme.spacing(1)
  },
  hint: {
    display: "block",
    marginBottom: theme.spacing(1)
  },
  totalsBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  // Fundo claro fixo com a cor de texto padrão fica ilegível no tema escuro
  // (texto branco sobre azul/verde claro): as duas pontas seguem o theme.type,
  // e o ícone herda a cor do label.
  chipReceived: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(33, 150, 243, 0.24)" : "#e3f2fd",
    color: theme.palette.type === "dark" ? "#90caf9" : "#0d47a1",
    fontWeight: 500,
    "& .MuiChip-icon": { color: "inherit" }
  },
  chipSent: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(76, 175, 80, 0.24)" : "#e8f5e9",
    color: theme.palette.type === "dark" ? "#a5d6a7" : "#1b5e20",
    fontWeight: 500,
    "& .MuiChip-icon": { color: "inherit" }
  },
  chipTotal: {
    fontWeight: 500
  },
  tableWrapper: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto"
  },
  table: {
    width: "100%",
    tableLayout: "fixed"
  },
  ellipsisCell: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  deletedText: {
    textDecoration: "line-through",
    opacity: 0.7
  },
  secondaryLine: {
    display: "block",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  paginationWrapper: {
    borderTop: "1px solid rgba(224, 224, 224, 1)",
    overflowX: "auto"
  }
}));

const getTodayDateInputValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";

  // O backend devolve DATETIME como string ("YYYY-MM-DD HH:mm:ss.ffffff") por
  // causa do dateStrings da conexão; o Safari não parseia esse formato, então a
  // troca do espaço por "T" mantém o comportamento igual em todos os browsers.
  const parsed = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return "-";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  }).format(parsed);
};

const contactOptionLabel = (option) => {
  if (!option) return "";
  if (option.name && option.number) return `${option.name} (${option.number})`;
  return option.name || option.number || "";
};

const MessagesReport = () => {
  const classes = useStyles();
  const [todayDate] = useState(() => getTodayDateInputValue());

  const [messages, setMessages] = useState([]);
  const [totals, setTotals] = useState({ total: 0, recebidas: 0, enviadas: 0 });
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [searchParam, setSearchParam] = useState("");
  const [startDate, setStartDate] = useState(todayDate);
  const [endDate, setEndDate] = useState(todayDate);
  const [direction, setDirection] = useState("all");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedContactTagIds, setSelectedContactTagIds] = useState([]);
  const [selectedQueueId, setSelectedQueueId] = useState("");
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketUuid, setTicketUuid] = useState(null);
  const [jumpToMessageId, setJumpToMessageId] = useState(null);
  const [jumpToMessageTs, setJumpToMessageTs] = useState(null);

  const userOptions = useMemo(() => [getNoUserOption(), ...users], [users]);

  useEffect(() => {
    let isMounted = true;

    const fetchFilterOptions = async () => {
      try {
        const [{ data: usersData }, { data: queuesData }, { data: whatsappsData }] =
          await Promise.all([
            api.get("/users/list"),
            api.get("/queue"),
            api.get("/whatsapp")
          ]);

        if (!isMounted) return;

        setUsers(Array.isArray(usersData) ? usersData : []);
        setQueues(Array.isArray(queuesData) ? queuesData : []);
        setWhatsapps(Array.isArray(whatsappsData) ? whatsappsData : []);
      } catch (err) {
        toastError(err);
      }
    };

    fetchFilterOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  // Busca de contatos no servidor (o Autocomplete não refiltra localmente, ver
  // filterOptions abaixo) — mesmo padrão da tela de Contatos.
  useEffect(() => {
    const term = contactSearch.trim();
    if (term.length < MIN_TERM_CHARS) {
      setContactOptions([]);
      return undefined;
    }

    let isMounted = true;
    setLoadingContacts(true);

    const debounce = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          // searchScope: busca só no cadastro do contato, sem varrer o corpo das
          // mensagens (que traria quem apenas citou a palavra).
          params: { searchParam: term, pageNumber: 1, searchScope: "contact" }
        });

        if (!isMounted) return;
        setContactOptions(Array.isArray(data?.contacts) ? data.contacts : []);
      } catch (err) {
        if (isMounted) toastError(err);
      } finally {
        if (isMounted) setLoadingContacts(false);
      }
    }, 400);

    return () => {
      isMounted = false;
      clearTimeout(debounce);
    };
  }, [contactSearch]);

  const buildParams = useCallback(() => {
    const params = { startDate, endDate };

    const term = searchParam.trim();
    if (term.length >= MIN_TERM_CHARS) params.searchParam = term;
    if (direction !== "all") params.direction = direction;
    if (selectedUsers.length) {
      params.userIds = JSON.stringify(selectedUsers.map((user) => user.id));
    }
    if (selectedContacts.length) {
      params.contactIds = JSON.stringify(selectedContacts.map((contact) => contact.id));
    }
    if (selectedContactTagIds.length) {
      params.contactTags = JSON.stringify(selectedContactTagIds);
    }
    if (selectedQueueId) params.queueIds = JSON.stringify([Number(selectedQueueId)]);
    if (selectedWhatsappId) params.whatsappIds = JSON.stringify([Number(selectedWhatsappId)]);
    if (includeDeleted) params.includeDeleted = true;

    return params;
  }, [
    startDate,
    endDate,
    searchParam,
    direction,
    selectedUsers,
    selectedContacts,
    selectedContactTagIds,
    selectedQueueId,
    selectedWhatsappId,
    includeDeleted
  ]);

  const fetchMessages = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);

    try {
      const { data } = await api.get("/messages/report", {
        params: { ...buildParams(), pageNumber: pageNumber + 1, pageSize: rowsPerPage }
      });

      const fetched = Array.isArray(data?.messages) ? data.messages : [];

      setMessages(fetched);
      // Os totais só vêm na primeira página; nas seguintes mantém o valor.
      if (data?.totals) setTotals(data.totals);
    } catch (err) {
      toastError(err);
      setMessages([]);
      setTotals({ total: 0, recebidas: 0, enviadas: 0 });
    } finally {
      setLoading(false);
    }
  }, [buildParams, startDate, endDate, pageNumber, rowsPerPage]);

  // Qualquer mudança de filtro volta para a primeira página. Como as deps de
  // buildParams são exatamente os filtros da tela, este efeito cobre todos.
  useEffect(() => {
    setPageNumber(0);
  }, [buildParams]);

  // O debounce evita um request por tecla digitada no campo de termo.
  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchMessages();
    }, 400);

    return () => clearTimeout(debounce);
  }, [fetchMessages]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNumber(0);
  };

  const handleClearFilters = () => {
    setPageNumber(0);
    setSearchParam("");
    setStartDate(todayDate);
    setEndDate(todayDate);
    setDirection("all");
    setSelectedUsers([]);
    setSelectedContacts([]);
    setSelectedContactTagIds([]);
    setSelectedQueueId("");
    setSelectedWhatsappId("");
    setIncludeDeleted(false);
    setContactSearch("");
  };

  const handleSelectedContactTags = useCallback((selecteds) => {
    setSelectedContactTagIds(selecteds.map((tag) => tag.id));
  }, []);

  const handleOpenAtMessage = (row) => {
    if (!row?.ticket?.uuid) {
      toast.error(i18n.t("messagesReport.toasts.openFailed"));
      return;
    }

    setJumpToMessageId(String(row.id));
    // Timestamp novo a cada clique: é o que faz o Ticket rolar de novo até a
    // mensagem quando a mesma linha é aberta duas vezes.
    setJumpToMessageTs(Date.now());
    setTicketUuid(row.ticket.uuid);
    setTicketModalOpen(true);
  };

  const handleCloseTicketModal = () => {
    setTicketModalOpen(false);
    setTicketUuid(null);
    setJumpToMessageId(null);
    setJumpToMessageTs(null);
  };

  const handleExport = async () => {
    setExporting(true);

    try {
      const { data } = await api.get("/messages/report", {
        params: { ...buildParams(), exportAll: true }
      });

      const fetched = Array.isArray(data?.messages) ? data.messages : [];

      if (!fetched.length) {
        toast.info(i18n.t("messagesReport.toasts.nothingToExport"));
        return;
      }

      const yes = i18n.t("common.yes");
      const no = i18n.t("common.no");
      const rows = fetched.map((row) => ({
        [i18n.t("messagesReport.table.dateTime")]: formatDateTime(row.createdAt),
        [i18n.t("messagesReport.table.direction")]: row.fromMe
          ? i18n.t("messagesReport.directionLabels.sent")
          : i18n.t("messagesReport.directionLabels.received"),
        [i18n.t("messagesReport.table.contact")]: row.contact?.name || row.contact?.number || "",
        [i18n.t("messagesReport.export.number")]: row.contact?.number || "",
        [i18n.t("messagesReport.export.group")]: row.contact?.isGroup ? yes : no,
        [i18n.t("messagesReport.export.groupAuthor")]: row.senderName || "",
        [i18n.t("messagesReport.table.user")]: row.userName || "",
        [i18n.t("messagesReport.export.signature")]: row.signName || "",
        [i18n.t("messagesReport.export.queue")]: row.queueName || "",
        [i18n.t("messagesReport.export.whatsapp")]: row.whatsappName || "",
        [i18n.t("messagesReport.table.message")]: row.body || getMessagePreview(row),
        [i18n.t("messagesReport.export.transcription")]: row.transcription || "",
        [i18n.t("messagesReport.export.deleted")]: row.isDeleted ? yes : no,
        [i18n.t("messagesReport.export.ticket")]: row.ticketId
      }));

      exportToExcel(
        rows,
        i18n.t("messagesReport.export.fileName", { start: startDate, end: endDate }),
        i18n.t("messagesReport.export.sheetName")
      );

      if (data?.hasMore) {
        toast.warn(i18n.t("messagesReport.toasts.exportLimited"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("messagesReport.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="outlined" onClick={handleClearFilters}>
            {i18n.t("messagesReport.buttons.clearFilters")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={handleExport}
            disabled={exporting || loading || !messages.length}
          >
            {exporting ? i18n.t("messagesReport.buttons.exporting") : i18n.t("common.exportExcel")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={1} className={classes.filtersRow}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label={i18n.t("messagesReport.filters.term")}
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              helperText={i18n.t("validation.minLength", { count: MIN_TERM_CHARS })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label={i18n.t("common.from")}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label={i18n.t("common.to")}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              label={i18n.t("messagesReport.table.direction")}
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
            >
              <MenuItem value="all">{i18n.t("messagesReport.filters.all")}</MenuItem>
              <MenuItem value="sent">{i18n.t("messagesReport.filters.sent")}</MenuItem>
              <MenuItem value="received">{i18n.t("messagesReport.filters.received")}</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={userOptions}
              value={selectedUsers}
              onChange={(event, value) => setSelectedUsers(value || [])}
              getOptionLabel={(option) => option?.name || ""}
              getOptionSelected={(option, value) => option.id === value.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("messagesReport.filters.user")}
                  placeholder={i18n.t("common.all")}
                />
              )}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1} className={classes.filtersRow}>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={contactOptions}
              value={selectedContacts}
              loading={loadingContacts}
              // Busca é server-side: o MUI não pode refiltrar o que já veio.
              filterOptions={(options) => options}
              onChange={(event, value) => setSelectedContacts(value || [])}
              getOptionLabel={contactOptionLabel}
              getOptionSelected={(option, value) => option.id === value.id}
              noOptionsText={
                contactSearch.trim().length < MIN_TERM_CHARS
                  ? i18n.t("messagesReport.filters.typeMinChars", { min: MIN_TERM_CHARS })
                  : i18n.t("messagesReport.filters.noContacts")
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option.name || option.number}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("messagesReport.table.contact")}
                  placeholder={i18n.t("common.all")}
                  onChange={(e) => setContactSearch(e.target.value || "")}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TagsFilter
              compact
              variant="standard"
              label={i18n.t("messagesReport.filters.contactTags")}
              maxVisibleRows={2.5}
              placeholder={i18n.t("messagesReport.filters.all")}
              selectedIds={selectedContactTagIds}
              onFiltered={handleSelectedContactTags}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              label={i18n.t("messagesReport.filters.queue")}
              value={selectedQueueId}
              onChange={(e) => setSelectedQueueId(e.target.value)}
            >
              <MenuItem value="">{i18n.t("messagesReport.filters.all")}</MenuItem>
              {/* 0 é o sentinela "Sem fila" tratado no backend. Escolher uma
                  fila específica exclui os sem fila (corte estrito). */}
              <MenuItem value="0">{i18n.t("messagesReport.noQueue")}</MenuItem>
              {queues.map((queue) => (
                <MenuItem key={queue.id} value={String(queue.id)}>
                  {queue.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              label={i18n.t("messagesReport.filters.whatsapp")}
              value={selectedWhatsappId}
              onChange={(e) => setSelectedWhatsappId(e.target.value)}
            >
              <MenuItem value="">{i18n.t("messagesReport.filters.all")}</MenuItem>
              {whatsapps.map((whatsapp) => (
                <MenuItem key={whatsapp.id} value={String(whatsapp.id)}>
                  {whatsapp.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                  color="primary"
                />
              }
              label={i18n.t("messagesReport.filters.includeDeleted")}
            />
          </Grid>
        </Grid>

        <Typography variant="caption" color="textSecondary" className={classes.hint}>
          {i18n.t("messagesReport.hint")}
        </Typography>

        <div className={classes.totalsBar}>
          <Chip
            label={i18n.t("messagesReport.totals.total", { value: totals.total })}
            size="small"
            className={classes.chipTotal}
          />
          <Chip
            label={i18n.t("messagesReport.totals.received", { value: totals.recebidas })}
            size="small"
            className={classes.chipReceived}
          />
          <Chip
            label={i18n.t("messagesReport.totals.sent", { value: totals.enviadas })}
            size="small"
            className={classes.chipSent}
          />
        </div>

        <div className={classes.tableWrapper}>
          <Table size="small" stickyHeader className={classes.table}>
            <colgroup>
              <col style={{ width: "150px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "200px" }} />
              <col style={{ width: "180px" }} />
              <col />
              <col style={{ width: "150px" }} />
              <col style={{ width: "80px" }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("messagesReport.table.dateTime")}</TableCell>
                <TableCell>{i18n.t("messagesReport.table.direction")}</TableCell>
                <TableCell>{i18n.t("messagesReport.table.contact")}</TableCell>
                <TableCell>{i18n.t("messagesReport.table.user")}</TableCell>
                <TableCell>{i18n.t("messagesReport.table.message")}</TableCell>
                <TableCell>{i18n.t("messagesReport.table.queueConnection")}</TableCell>
                <TableCell align="center">{i18n.t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRowSkeleton columns={7} />
              ) : (
                <>
                  {messages.map((row) => {
                    const preview = getMessagePreview(row);

                    return (
                      <TableRow key={row.id} hover>
                        <TableCell className={classes.ellipsisCell}>
                          {formatDateTime(row.createdAt)}
                        </TableCell>

                        <TableCell>
                          {row.fromMe ? (
                            <Chip
                              size="small"
                              icon={<CallMadeIcon />}
                              label={i18n.t("messagesReport.directionLabels.sent")}
                              className={classes.chipSent}
                            />
                          ) : (
                            <Chip
                              size="small"
                              icon={<CallReceivedIcon />}
                              label={i18n.t("messagesReport.directionLabels.received")}
                              className={classes.chipReceived}
                            />
                          )}
                        </TableCell>

                        <TableCell className={classes.ellipsisCell}>
                          {row.contact?.name || row.contact?.number || "-"}
                          {row.contact?.isGroup && row.senderName && (
                            <Typography variant="caption" className={classes.secondaryLine}>
                              {`${i18n.t("messagesReport.table.inGroup")} ${row.senderName}`}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell className={classes.ellipsisCell}>
                          {row.userName || i18n.t("messagesReport.noUser")}
                          {row.signName && row.signName !== row.userName && (
                            <Typography variant="caption" className={classes.secondaryLine}>
                              {`${i18n.t("messagesReport.table.typedBy")} ${row.signName}`}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell className={classes.ellipsisCell}>
                          <Tooltip title={preview}>
                            <span className={row.isDeleted ? classes.deletedText : undefined}>
                              {preview}
                            </span>
                          </Tooltip>
                        </TableCell>

                        <TableCell className={classes.ellipsisCell}>
                          {row.queueName || i18n.t("messagesReport.noQueue")}
                          {row.whatsappName && (
                            <Typography variant="caption" className={classes.secondaryLine}>
                              {row.whatsappName}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title={i18n.t("messagesReport.table.openAtMessage")}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={!row.ticket?.uuid}
                                onClick={() => handleOpenAtMessage(row)}
                                aria-label={i18n.t("messagesReport.table.openAtMessage")}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {!messages.length && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        {i18n.t("messagesReport.table.empty")}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </div>

        <div className={classes.paginationWrapper}>
          <TablePagination
            component="div"
            count={totals.total}
            page={pageNumber}
            onPageChange={(_event, newPage) => setPageNumber(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
            labelRowsPerPage={i18n.t("messagesReport.pagination.rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              i18n.t("messagesReport.pagination.displayedRows", { from, to, total: count })
            }
            ActionsComponent={TablePaginationActions}
          />
        </div>

        <TicketModal
          open={ticketModalOpen}
          onClose={handleCloseTicketModal}
          ticketId={ticketUuid}
          jumpToMessageId={jumpToMessageId}
          jumpToMessageTs={jumpToMessageTs}
          messageHistoryModeOverride="ticket"
        />
      </Paper>
    </MainContainer>
  );
};

export default MessagesReport;
